import React, { useMemo } from 'react';
import DataSection from './DataSection';
import styles from '../TradeFlowTrackingTab.module.css';
import {
  LiveGateDecision,
  LiveGateDecisionsParams,
  getLiveGateDecisions,
} from '../../../services/tradeFlowAdminApi';
import { useFlowData } from '../hooks/useTradeFlowData';

interface LiveGateSectionProps {
  dateFrom: string;
  dateTo: string;
  symbol: string;
}

const LiveGateSection: React.FC<LiveGateSectionProps> = ({ dateFrom, dateTo, symbol }) => {
  const [recIdFilter, setRecIdFilter] = React.useState('');

  const params = useMemo<LiveGateDecisionsParams>(() => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    symbol: symbol || undefined,
    recommendation_id: recIdFilter || undefined,
  }), [dateFrom, dateTo, symbol, recIdFilter]);

  const { data, total, page, setPage, isLoading, isFetching, isError, error, totalPages } = useFlowData<LiveGateDecision, LiveGateDecisionsParams>({
    queryKeyBase: ['admin', 'trade-flow', 'live-gate'],
    fetchFn: (p) => getLiveGateDecisions(p).then(r => r),
    params,
    refetchInterval: 10000,
  });

  return (
    <DataSection
      title="Live Entry Gate v2"
      tabNumber={8}
      description="Real-time entry filter decisions applied just before execution. Includes 23 individual gate filters."
      loading={isLoading}
      isFetching={isFetching}
      error={error?.message || null}
      dataCount={data.length}
      page={page}
      totalPages={totalPages}
      total={total}
      onPageChange={setPage}
    >
      <div className={styles.filterBar}>
        <input
          type="text"
          placeholder="Filter by Recommendation ID..."
          value={recIdFilter}
          onChange={(e) => { setRecIdFilter(e.target.value); setPage(1); }}
          className={styles.filterInput}
        />
      </div>

      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Time</th>
            <th>Symbol</th>
            <th>Rec ID</th>
            <th>Filter</th>
            <th>Result</th>
            <th>Value</th>
            <th>Threshold</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.id}>
              <td className={styles.monoSmall}>
                {d.evaluated_at ? new Date(d.evaluated_at).toLocaleString() : '-'}
              </td>
              <td className={styles.mono}>{d.symbol}</td>
              <td className={styles.monoSmall}>{d.recommendation_id.slice(0, 12)}...</td>
              <td>{d.filter_name}</td>
              <td>
                <span className={d.passed ? styles.badgePass : styles.badgeFail}>
                  {d.passed ? 'PASS' : 'FAIL'}
                </span>
              </td>
              <td className={styles.mono}>
                {d.actual_value !== null ? d.actual_value.toFixed(6) : '-'}
              </td>
              <td className={styles.mono}>
                {d.threshold_low !== null ? d.threshold_low.toFixed(6) : '-'}
              </td>
              <td className={styles.detailCell}>{d.detail || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </DataSection>
  );
};

export default LiveGateSection;
